import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { I18n } from "react-redux-i18n";
import { Card, CardBody, CardHeader, CardTitle } from 'reactstrap';
import { Row, Col, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { generateCardPreview, printCard, batchPrintCards } from '../../../../store/actions/CardTemplateActions';
import swal from "sweetalert";

const CardPreview = ({ 
    cardId,
    cards = [],
    cardPreviews,
    loadingStates,
    error,
    generateCardPreview,
    printCard,
    batchPrintCards
}) => {
    const [selectedCards, setSelectedCards] = useState([]);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printOptions, setPrintOptions] = useState({
        quality: 'high',
        format: 'pdf',
        includeBackSide: true
    });

    useEffect(() => {
        if (cardId && !cardPreviews[cardId]) {
            generateCardPreview(cardId);
        }
    }, [cardId, cardPreviews, generateCardPreview]);

    const handleGeneratePreview = async (id) => {
        const result = await generateCardPreview(id);
        if (!result.success) {
            swal(I18n.t('common.error'), result.error, "error");
        }
    };

    const handlePrintCard = async (id) => {
        const result = await printCard(id, {
            ...printOptions,
            cardNumber: getCardNumber(id)
        });
        
        if (result.success) {
            swal(I18n.t('common.success'), I18n.t('cardTemplate.success.cardPrinted'), "success");
        } else {
            swal(I18n.t('common.error'), result.error, "error");
        }
    };

    const handleBatchPrint = async () => {
        if (selectedCards.length === 0) {
            swal(I18n.t('common.error'), I18n.t('cardTemplate.error.noCardsSelected'), "error");
            return;
        }

        const result = await batchPrintCards(selectedCards, printOptions);
        
        if (result.success) {
            swal(I18n.t('common.success'), I18n.t('cardTemplate.success.batchPrinted'), "success");
            setSelectedCards([]);
            setShowPrintModal(false);
        } else {
            swal(I18n.t('common.error'), result.error, "error");
        }
    };

    const handleCardSelection = (id, isSelected) => {
        if (isSelected) {
            setSelectedCards([...selectedCards, id]);
        } else {
            setSelectedCards(selectedCards.filter(cardId => cardId !== id));
        }
    };

    const handleSelectAll = () => {
        const allCardIds = cards.map(card => card.id || card.cardId);
        setSelectedCards(selectedCards.length === allCardIds.length ? [] : allCardIds);
    };

    const getCardNumber = (id) => {
        const card = cards.find(c => (c.id || c.cardId) === id);
        return card?.cardNumber || `CARD_${id}`;
    };

    const getCardInfo = (id) => {
        const card = cards.find(c => (c.id || c.cardId) === id);
        return card || {};
    };

    const renderPreviewCard = (id) => {
        const previewUrl = cardPreviews[id];
        const cardInfo = getCardInfo(id);
        const isSelected = selectedCards.includes(id);
        const isGeneratingPreview = loadingStates.generateCardPreview;

        return (
            <Col lg={6} xl={4} key={id} className="mb-4">
                <Card className={`card-preview ${isSelected ? 'border-primary' : ''}`}>
                    <CardHeader className="d-flex justify-content-between align-items-center">
                        <div>
                            <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleCardSelection(id, e.target.checked)}
                                className="me-2"
                            />
                            <strong>{getCardNumber(id)}</strong>
                        </div>
                        <div>
                            <Button
                                color="outline-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => handleGeneratePreview(id)}
                                disabled={isGeneratingPreview}
                            >
                                {isGeneratingPreview ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <i className="fa fa-refresh"></i>
                                )}
                            </Button>
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() => handlePrintCard(id)}
                                disabled={loadingStates.printCard}
                            >
                                {loadingStates.printCard ? (
                                    <Spinner size="sm" />
                                ) : (
                                    <i className="fa fa-print"></i>
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardBody>
                        {cardInfo.beneficiaryName && (
                            <div className="mb-2">
                                <small className="text-muted">{I18n.t('cardTemplate.field.beneficiaryName')}</small>
                                <div>{cardInfo.beneficiaryName}</div>
                            </div>
                        )}
                        
                        <div className="card-preview-container" style={{ minHeight: '200px' }}>
                            {previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt={`Aperçu ${getCardNumber(id)}`}
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '200px', objectFit: 'contain' }}
                                />
                            ) : isGeneratingPreview ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                                    <Spinner color="primary" />
                                    <span className="ms-2">{I18n.t('cardTemplate.generating')}</span>
                                </div>
                            ) : (
                                <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '200px' }}>
                                    <div className="text-center">
                                        <i className="fa fa-image fa-3x text-muted mb-2"></i>
                                        <div className="text-muted">{I18n.t('cardTemplate.noPreview')}</div>
                                        <Button
                                            color="outline-primary"
                                            size="sm"
                                            className="mt-2"
                                            onClick={() => handleGeneratePreview(id)}
                                        >
                                            {I18n.t('cardTemplate.generatePreview')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {cardInfo.status && (
                            <div className="mt-2">
                                <small className="text-muted">{I18n.t('cardTemplate.field.status')}</small>
                                <div>
                                    <span className={`badge ${getStatusBadgeColor(cardInfo.status)}`}>
                                        {I18n.t(`cardTemplate.status.${cardInfo.status?.toLowerCase()}`)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Col>
        );
    };

    const getStatusBadgeColor = (status) => {
        const statusColors = {
            'DELIVERED': 'bg-success',
            'SHIPPED': 'bg-info',
            'PRINTING': 'bg-warning',
            'PENDING': 'bg-secondary',
            'CANCELLED': 'bg-danger'
        };
        return statusColors[status] || 'bg-secondary';
    };

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5>{I18n.t('common.error')}</h5>
                <p>{error}</p>
            </div>
        );
    }

    // Single card preview mode
    if (cardId && !cards?.length) {
        const previewUrl = cardPreviews[cardId];
        const isGenerating = loadingStates.generateCardPreview;

        return (
            <Card>
                <CardHeader>
                    <CardTitle>{I18n.t('cardTemplate.preview.title')}</CardTitle>
                    <div>
                        <Button
                            color="outline-primary"
                            className="me-2"
                            onClick={() => handleGeneratePreview(cardId)}
                            disabled={isGenerating}
                        >
                            {isGenerating ? <Spinner size="sm" /> : <i className="fa fa-refresh"></i>}
                            {I18n.t('cardTemplate.refresh')}
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => handlePrintCard(cardId)}
                            disabled={loadingStates.printCard}
                        >
                            {loadingStates.printCard ? <Spinner size="sm" /> : <i className="fa fa-print"></i>}
                            {I18n.t('cardTemplate.print')}
                        </Button>
                    </div>
                </CardHeader>
                <CardBody>
                    <div className="text-center">
                        {previewUrl ? (
                            <img
                                src={previewUrl}
                                alt={`Aperçu carte ${cardId}`}
                                className="img-fluid rounded shadow"
                                style={{ maxHeight: '400px' }}
                            />
                        ) : isGenerating ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                                <Spinner color="primary" />
                                <span className="ms-2">{I18n.t('cardTemplate.generating')}</span>
                            </div>
                        ) : (
                            <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '300px' }}>
                                <div className="text-center">
                                    <i className="fa fa-image fa-4x text-muted mb-3"></i>
                                    <div className="text-muted mb-3">{I18n.t('cardTemplate.noPreview')}</div>
                                    <Button
                                        color="primary"
                                        onClick={() => handleGeneratePreview(cardId)}
                                    >
                                        {I18n.t('cardTemplate.generatePreview')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </CardBody>
            </Card>
        );
    }

    // Multiple cards preview mode
    if (!cards?.length) {
        return (
            <div className="text-center py-5">
                <i className="fa fa-credit-card fa-4x text-muted mb-3"></i>
                <h5 className="text-muted">{I18n.t('cardTemplate.noCards')}</h5>
            </div>
        );
    }

    return (
        <div>
            {/* Batch Actions */}
            <Row className="mb-3">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <Button
                                color="outline-primary"
                                onClick={handleSelectAll}
                                className="me-2"
                            >
                                <i className="fa fa-check-square me-1"></i>
                                {selectedCards.length === cards.length ? 
                                    I18n.t('common.deselectAll') : 
                                    I18n.t('common.selectAll')
                                }
                            </Button>
                            {selectedCards.length > 0 && (
                                <span className="text-muted">
                                    {I18n.t('cardTemplate.selectedCount', { count: selectedCards.length })}
                                </span>
                            )}
                        </div>
                        <div>
                            {selectedCards.length > 0 && (
                                <Button
                                    color="primary"
                                    onClick={() => setShowPrintModal(true)}
                                    disabled={loadingStates.batchPrintCards}
                                >
                                    {loadingStates.batchPrintCards ? <Spinner size="sm" /> : <i className="fa fa-print"></i>}
                                    {I18n.t('cardTemplate.batchPrint')} ({selectedCards.length})
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Cards Grid */}
            <Row>
                {cards.map(card => renderPreviewCard(card.id || card.cardId))}
            </Row>

            {/* Batch Print Modal */}
            <Modal isOpen={showPrintModal} toggle={() => setShowPrintModal(false)}>
                <ModalHeader toggle={() => setShowPrintModal(false)}>
                    {I18n.t('cardTemplate.batchPrint')}
                </ModalHeader>
                <ModalBody>
                    <p>{I18n.t('cardTemplate.batchPrintConfirm', { count: selectedCards.length })}</p>
                    
                    <div className="mb-3">
                        <label className="form-label">{I18n.t('cardTemplate.printOptions.quality')}</label>
                        <select 
                            className="form-select"
                            value={printOptions.quality}
                            onChange={(e) => setPrintOptions({...printOptions, quality: e.target.value})}
                        >
                            <option value="high">{I18n.t('cardTemplate.quality.high')}</option>
                            <option value="medium">{I18n.t('cardTemplate.quality.medium')}</option>
                            <option value="low">{I18n.t('cardTemplate.quality.low')}</option>
                        </select>
                    </div>

                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="includeBackSide"
                            checked={printOptions.includeBackSide}
                            onChange={(e) => setPrintOptions({...printOptions, includeBackSide: e.target.checked})}
                        />
                        <label className="form-check-label" htmlFor="includeBackSide">
                            {I18n.t('cardTemplate.printOptions.includeBackSide')}
                        </label>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowPrintModal(false)}>
                        {I18n.t('common.cancel')}
                    </Button>
                    <Button 
                        color="primary" 
                        onClick={handleBatchPrint}
                        disabled={loadingStates.batchPrintCards}
                    >
                        {loadingStates.batchPrintCards && <Spinner size="sm" className="me-1" />}
                        {I18n.t('cardTemplate.print')}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => ({
    cardPreviews: state.cardTemplate.cardPreviews,
    loadingStates: state.cardTemplate.loadingStates,
    error: state.cardTemplate.error
});

const mapDispatchToProps = {
    generateCardPreview,
    printCard,
    batchPrintCards
};

export default connect(mapStateToProps, mapDispatchToProps)(CardPreview);